import { formatBytes, resolveMimeType, validateFileForUpload, type FileRules } from './file-utils'

/** What gets uploaded for a chosen file. */
export interface PreparedFile {
  /** The bytes to send: the original file, a smaller image, or a gzipped text file (original name and type). */
  file: File
  /** `gzip` when the bytes are compressed and the server must store them with that encoding. */
  contentEncoding?: 'gzip'
  /** Size of the file the person chose, so the server can show the real size of a gzipped file. */
  originalSize: number
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
/** Types worth gzipping; everything else is already compressed or not text-like. */
const TEXT_LIKE_TYPES = ['text/csv', 'text/plain', 'application/msword', 'application/vnd.ms-excel']

export const MAX_IMAGE_SIDE = 2560
export const SKIP_IMAGE_BELOW_BYTES = 300 * 1024
export const MIN_GZIP_BYTES = 32 * 1024
/** A result must be at least this much smaller to be used. */
const IMAGE_MIN_SAVING = 0.10
const GZIP_MIN_SAVING = 0.15

/** The browser calls the decision logic depends on, kept separate so tests can replace them. */
export interface CompressionDeps {
  /** Decodes an image honouring its EXIF orientation. */
  decodeImage(file: File): Promise<{ width: number; height: number; release(): void; bitmap: unknown }>
  /** Draws the decoded image at the given size and encodes it; null if the browser cannot encode the type. */
  encodeImage(decoded: { bitmap: unknown }, width: number, height: number, type: string, quality: number): Promise<Blob | null>
  /** Gzips the file; null where CompressionStream is not available. */
  gzip(file: File): Promise<Blob | null>
}

export const browserDeps: CompressionDeps = {
  async decodeImage(file) {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    return { width: bitmap.width, height: bitmap.height, bitmap, release: () => bitmap.close() }
  },
  async encodeImage(decoded, width, height, type, quality) {
    const canvas: HTMLCanvasElement | OffscreenCanvas =
      typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(width, height) : Object.assign(document.createElement('canvas'), { width, height })
    const context = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null
    if (!context) return null
    context.drawImage(decoded.bitmap as ImageBitmap, 0, 0, width, height)
    if ('convertToBlob' in canvas) return canvas.convertToBlob({ type, quality })
    return new Promise<Blob | null>((resolve) => (canvas as HTMLCanvasElement).toBlob(resolve, type, quality))
  },
  async gzip(file) {
    if (typeof CompressionStream === 'undefined') return null
    return new Response(file.stream().pipeThrough(new CompressionStream('gzip'))).blob()
  },
}

const EXTENSION_BY_TYPE: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' }

/** Same name with the extension of the new type (photo.heic stays out: only jpeg/png/webp get here). */
function renamedFor(name: string, type: string): string {
  const extension = EXTENSION_BY_TYPE[type]
  const dot = name.lastIndexOf('.')
  return `${dot > 0 ? name.slice(0, dot) : name}.${extension}`
}

function allows(rules: FileRules, type: string): boolean {
  const allowed = (rules.allowedTypes ?? []).filter(Boolean)
  return allowed.length === 0 || allowed.includes(type)
}

async function shrinkImage(file: File, type: string, rules: FileRules, deps: CompressionDeps): Promise<File | null> {
  const decoded = await deps.decodeImage(file)
  try {
    const longest = Math.max(decoded.width, decoded.height)
    if (file.size < SKIP_IMAGE_BELOW_BYTES && longest <= MAX_IMAGE_SIDE) return null

    const scale = longest > MAX_IMAGE_SIDE ? MAX_IMAGE_SIDE / longest : 1
    const width = Math.max(1, Math.round(decoded.width * scale))
    const height = Math.max(1, Math.round(decoded.height * scale))

    // JPEG stays JPEG; PNG becomes WebP when the field allows it (WebP keeps transparency), else stays PNG
    const [outType, quality] =
      type === 'image/jpeg' ? ['image/jpeg', 0.82]
      : type === 'image/webp' ? ['image/webp', 0.85]
      : allows(rules, 'image/webp') ? ['image/webp', 0.85]
      : ['image/png', 1]
    const blob = await deps.encodeImage(decoded, width, height, outType, quality)
    // Browsers silently fall back to PNG for types they cannot encode; only trust the type we asked for
    if (!blob || blob.type !== outType || blob.size > file.size * (1 - IMAGE_MIN_SAVING)) return null
    return new File([blob], renamedFor(file.name, outType), { type: outType, lastModified: file.lastModified })
  } finally {
    decoded.release()
  }
}

/**
 * Makes a chosen file cheaper to upload and store, falling back to the file as it is whenever
 * something is not worth it or fails.
 *
 * - Photos (JPEG, PNG, WebP; never GIF or SVG): large ones are scaled to at most 2560 px on the long
 *   side and re-encoded (JPEG q0.82, WebP q0.85; PNG becomes WebP when the field allows it). Used only
 *   if at least 10 % smaller. Re-encoding drops metadata such as GPS position.
 * - Text-like files (CSV, TXT, legacy DOC/XLS) of at least 32 KB: gzipped, with the original name and
 *   type; used only if at least 15 % smaller. The server stores them with Content-Encoding gzip.
 * - Everything else (PDF, DOCX/XLSX, ZIP, GIF, ...) is already compressed and is left alone.
 */
export async function prepareFileForUpload(
  file: File,
  rules: FileRules = {},
  deps: CompressionDeps = browserDeps
): Promise<PreparedFile> {
  const original: PreparedFile = { file, originalSize: file.size }
  const type = resolveMimeType(file)
  try {
    if (type && IMAGE_TYPES.includes(type)) {
      const smaller = await shrinkImage(file, type, rules, deps)
      return smaller ? { file: smaller, originalSize: file.size } : original
    }
    if (type && TEXT_LIKE_TYPES.includes(type) && file.size >= MIN_GZIP_BYTES) {
      const blob = await deps.gzip(file)
      if (blob && blob.size <= file.size * (1 - GZIP_MIN_SAVING)) {
        return {
          file: new File([blob], file.name, { type, lastModified: file.lastModified }),
          contentEncoding: 'gzip',
          originalSize: file.size,
        }
      }
    }
  } catch {
    // A file we cannot decode or compress is uploaded as it is
  }
  return original
}

/** "Saved 7.9 MB (−66 %)" when the prepared file is smaller than the chosen one, else null. */
export function describeSavings(originalSize: number, preparedSize: number): string | null {
  if (preparedSize >= originalSize || originalSize <= 0) return null
  const percent = Math.round((1 - preparedSize / originalSize) * 100)
  return `Saved ${formatBytes(originalSize - preparedSize)} (−${percent} %)`
}

/**
 * Checks a chosen file the way the upload will see it: its type as chosen, its size as prepared
 * (a 12 MB photo may fit a 10 MB limit once optimised). Returns a message or null.
 */
export function validatePrepared(original: File, prepared: PreparedFile, rules: FileRules): string | null {
  const typeProblem = validateFileForUpload(original, { allowedTypes: rules.allowedTypes })
  if (typeProblem) return typeProblem
  return validateFileForUpload(prepared.file, {
    maxFileSizeBytes: rules.maxFileSizeBytes,
    minFileSizeBytes: rules.minFileSizeBytes,
  })
}
